/**
 * Order actions replicating reware-admin's Orders portal (OrderMenuOptions.jsx):
 * Capture Payment as the primary button, plus a "⋮" menu with Apply order
 * refund, Cancel order, Initiate return, Free shipping, Issue gift card,
 * Extend returnability, Short ship, Create 3PL webhook event, Edit shipping
 * address, and Resend confirmation. Each is gated by the order's capability
 * flags — except Resend, which is always available — and a disabled item shows
 * its reason inline. All hit the same admin_graphql mutations the admin portal
 * uses.
 */
import { useState } from 'react';
import { useNotify, useRefresh } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  applyOrderRefund,
  cancelOrder,
  capturePayment,
  createWebhookEvent,
  extendReturnability,
  applyFreeShipping,
  initiateReturn,
  issueGiftcard,
  sendUpdatedConfirmation,
  shortShipOrder,
  updateShippingAddress,
} from './mutations';
import { present } from './types';
import type { ConstantOption, OrderRecord } from './types';

interface OrderActionsProps {
  order: OrderRecord;
  refundReasons: ConstantOption[];
  giftcardReasons: ConstantOption[];
  returnReasons: ConstantOption[];
  freeShippingReasons: ConstantOption[];
}

type DialogKind = null | 'refund' | 'cancel' | 'giftcard' | 'freeShipping' | 'address' | 'return';

const canCapture = (order: OrderRecord): boolean =>
  !order.orderCapturedAt && (order.total ?? 0) - (order.giftCardTotal ?? 0) > 0 && order.state !== 'CANCELED';

export const OrderActions = ({
  order,
  refundReasons,
  giftcardReasons,
  returnReasons,
  freeShippingReasons,
}: OrderActionsProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<DialogKind>(null);

  // dialog form state
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundComments, setRefundComments] = useState('');
  const [giftcardAmount, setGiftcardAmount] = useState('');
  const [giftcardEmail, setGiftcardEmail] = useState(order.deliveryAddress?.email ?? '');
  const [giftcardReason, setGiftcardReason] = useState('');
  const [freeShippingReason, setFreeShippingReason] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [returnComment, setReturnComment] = useState('');
  const [address, setAddress] = useState({
    address1: order.deliveryAddress?.address1 ?? '',
    address2: order.deliveryAddress?.address2 ?? '',
    city: order.deliveryAddress?.city ?? '',
    state: order.deliveryAddress?.state ?? '',
    postalCode: order.deliveryAddress?.postalCode ?? '',
  });

  /**
   * Separate success/failure labels rather than `${label} failed`, which turned the success
   * phrasing into "Payment captured failed".
   *
   * The dialog closes on success only. Closing it in `finally` discarded the amount, reason and
   * comments the operator had typed the moment the server rejected the request, leaving them to
   * re-enter everything with no view of what was sent.
   */
  const run = async (
    labels: { success: string, failure: string },
    action: () => Promise<unknown>
  ) => {
    setBusy(true);
    try {
      await action();
      notify(labels.success, { type: 'success' });
      setDialog(null);
      setMenuAnchor(null);
      refresh();
    } catch (error) {
      // Falls back on an empty message too: an HttpError built from a missing status text is
      // still an Error, so `instanceof` alone would notify ''.
      const message = error instanceof Error && error.message ? error.message : labels.failure;
      console.error('Order action failed', { orderId: order.id, action: labels.success, error });
      notify(message, { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  // Gate → disabled-reason string (null means enabled).
  const stateReason = 'Not available in the order’s current state';
  const cancelReason = order.isOrderLocked ? "Locked after order is created in 3PL's WMS" : order.canBeCanceled ? null : stateReason;

  // Items carrying an inventory-item uuid, which initiateReturns matches on. The order-level
  // canBeReturned flag is state-based and does not imply any item is still returnable, so both
  // gates are needed or the action is offered for an empty return.
  const returnableItems = present(order.orderItems)
    .filter(item => item.canBeReturned && item.inventoryItem?.uuid)
    .map(item => ({ id: item.id, inventoryItemUuid: item.inventoryItem?.uuid ?? '' }));
  const returnDisabledReason = !order.canBeReturned ?
    stateReason :
    !returnableItems.length ? 'No items on this order can be returned' : null;

  interface MenuAction {
    key: string;
    label: string;
    reason: string | null;
    onClick: () => void;
  }

  const menuActions: MenuAction[] = [
    { key: 'refund', label: 'Apply order refund', reason: order.canBeDiscounted ? null : stateReason, onClick: () => { setMenuAnchor(null); setDialog('refund'); } },
    { key: 'cancel', label: 'Cancel order', reason: cancelReason, onClick: () => { setMenuAnchor(null); setDialog('cancel'); } },
    { key: 'return', label: 'Initiate return', reason: returnDisabledReason, onClick: () => { setMenuAnchor(null); setDialog('return'); } },
    { key: 'freeShipping', label: 'Free shipping', reason: order.canHaveFreeShipping ? null : stateReason, onClick: () => { setMenuAnchor(null); setDialog('freeShipping'); } },
    { key: 'giftcard', label: 'Issue gift card', reason: order.canIssueGiftcard ? null : stateReason, onClick: () => { setMenuAnchor(null); setDialog('giftcard'); } },
    { key: 'extend', label: 'Extend returnability', reason: order.canExtendReturnability ? null : stateReason, onClick: () => run({ success: 'Returnability extended', failure: "Couldn't extend returnability" }, () => extendReturnability(order.id)) },
    { key: 'shortShip', label: 'Short ship order', reason: order.canBeShortShipped ? null : stateReason, onClick: () => run({ success: 'Order short shipped', failure: "Couldn't short ship the order" }, () => shortShipOrder(order.id)) },
    { key: 'webhook', label: 'Create 3PL webhook event', reason: order.is3plOrderStalled ? null : 'Only for stalled 3PL orders', onClick: () => run({ success: 'Webhook event created', failure: "Couldn't create the webhook event" }, () => createWebhookEvent(order.id)) },
    // Backstop, not the primary defence. The parse is deliberately lenient about primitives
    // (see addressField in types.ts) precisely so an editable address is not blanked over a
    // numeric postcode. This covers what is left — a scalar that is not an object, or a field
    // holding a nested object — where every input would render empty and Save would merge those
    // blanks over the stored address.
    { key: 'address', label: 'Edit shipping address', reason: order.deliveryAddress ? null : "Can't read this order's address", onClick: () => { setMenuAnchor(null); setDialog('address'); } },
    { key: 'resend', label: 'Resend confirmation', reason: null, onClick: () => run({ success: 'Confirmation email sent', failure: "Couldn't send the confirmation email" }, () => sendUpdatedConfirmation(order.id)) },
  ];

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
      {canCapture(order) && (
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={busy}
          onClick={() => run({ success: 'Payment captured', failure: "Couldn't capture the payment" }, () => capturePayment(order.id))}
        >
          Capture Payment
        </Button>
      )}
      <IconButton
        size="small"
        onClick={event => setMenuAnchor(event.currentTarget)}
        aria-label="Order actions"
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {/* The reason renders inline rather than as a tooltip. A native `title` never fires on a
            disabled MenuItem (MUI sets pointer-events: none), and wrapping each item in a
            Tooltip + <span> breaks the menu outright: MenuList.moveFocus skips any child without
            a tabindex, so arrow keys and type-ahead stop working on the ENABLED items too.
            Inline text also reaches screen readers, which a hover tooltip does not. */}
        {menuActions.map(action => (
          <MenuItem
            key={action.key}
            disabled={busy || !!action.reason}
            onClick={action.onClick}
            sx={{ display: 'block', py: 0.75 }}
          >
            <Typography variant="body2">{action.label}</Typography>
            {action.reason && (
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                {action.reason}
              </Typography>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Apply order refund */}
      <Dialog open={dialog === 'refund'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{`Refund order ${order.uuid.split('-')[0].toUpperCase()}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={`Refund amount (${order.transactionCurrencyCode || 'USD'})`} value={refundAmount} onChange={e => setRefundAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            <TextField select label="Reason" value={refundReason} onChange={e => setRefundReason(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
              <option value="" disabled>Select a reason</option>
              {refundReasons.map(r => <option key={r.code} value={r.code}>{r.displayName}</option>)}
            </TextField>
            <TextField label="Comments (optional)" value={refundComments} onChange={e => setRefundComments(e.target.value)} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !refundReason || !(parseFloat(refundAmount) > 0)}
            onClick={() => run({ success: 'Order refund applied', failure: "Couldn't apply the refund" }, () => applyOrderRefund({ orderId: order.id, orderLevelRefund: parseFloat(refundAmount), refundReason, comments: refundComments || undefined }))}
          >
            Apply refund
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel order */}
      <Dialog open={dialog === 'cancel'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel order?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {`This cancels order ${order.uuid.split('-')[0].toUpperCase()} and releases its items. This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Keep order</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={() => run({ success: 'Order canceled', failure: "Couldn't cancel the order" }, () => cancelOrder(order.id))}>
            Cancel order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Initiate return */}
      <Dialog open={dialog === 'return'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Initiate return</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {`Returns all ${returnableItems.length} returnable ${returnableItems.length === 1 ? 'item' : 'items'} on this order with free return shipping. The customer is emailed a return label.`}
            </Typography>
            <TextField select label="Reason" value={returnReason} onChange={e => setReturnReason(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
              <option value="" disabled>Select a reason</option>
              {returnReasons.map(r => <option key={r.code} value={r.code}>{r.displayName}</option>)}
            </TextField>
            <TextField label="Comment (optional)" value={returnComment} onChange={e => setReturnComment(e.target.value)} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !returnReason}
            onClick={() => run(
              { success: 'Return initiated', failure: "Couldn't initiate the return" },
              () => initiateReturn(order.id, returnableItems, returnReason, returnComment || undefined)
            )}
          >
            Initiate return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue gift card */}
      <Dialog open={dialog === 'giftcard'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Issue gift card</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={`Amount (${order.transactionCurrencyCode || 'USD'})`} value={giftcardAmount} onChange={e => setGiftcardAmount(e.target.value)} inputProps={{ inputMode: 'decimal' }} />
            <TextField label="Recipient email" value={giftcardEmail} onChange={e => setGiftcardEmail(e.target.value)} />
            <TextField select label="Reason" value={giftcardReason} onChange={e => setGiftcardReason(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}>
              <option value="" disabled>Select a reason</option>
              {giftcardReasons.map(r => <option key={r.code} value={r.code}>{r.displayName}</option>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !giftcardReason || !giftcardEmail || !(parseFloat(giftcardAmount) > 0)}
            onClick={() => run({ success: 'Gift card issued', failure: "Couldn't issue the gift card" }, () => issueGiftcard({ orderId: order.id, amount: parseFloat(giftcardAmount), email: giftcardEmail, reason: giftcardReason }))}
          >
            Issue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Free shipping */}
      <Dialog open={dialog === 'freeShipping'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Apply free shipping</DialogTitle>
        <DialogContent>
          {/* freeShippingReasons, not refundReasons — reware-api exposes a distinct
              application.constants.freeShipping.reasons list for this dialog. */}
          <TextField select fullWidth label="Reason" value={freeShippingReason} onChange={e => setFreeShippingReason(e.target.value)} SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ mt: 1 }}>
            <option value="" disabled>Select a reason</option>
            {freeShippingReasons.map(r => <option key={r.code} value={r.code}>{r.displayName}</option>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !freeShippingReason} onClick={() => run({ success: 'Free shipping applied', failure: "Couldn't apply free shipping" }, () => applyFreeShipping(order.id, freeShippingReason))}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit shipping address */}
      <Dialog open={dialog === 'address'} onClose={() => setDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>Edit shipping address</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Address 1" value={address.address1} onChange={e => setAddress({ ...address, address1: e.target.value })} />
            <TextField label="Address 2" value={address.address2} onChange={e => setAddress({ ...address, address2: e.target.value })} />
            <TextField label="City" value={address.city} onChange={e => setAddress({ ...address, city: e.target.value })} />
            <Stack direction="row" spacing={2}>
              <TextField label="State" value={address.state} onChange={e => setAddress({ ...address, state: e.target.value })} />
              <TextField label="Postal Code" value={address.postalCode} onChange={e => setAddress({ ...address, postalCode: e.target.value })} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={busy || !address.address1.trim() || !address.city.trim() || !address.postalCode.trim()}
            onClick={() => run(
              { success: 'Shipping address updated', failure: "Couldn't update the shipping address" },
              // Blank optional fields are omitted rather than sent as '': the server merges the
              // payload over the stored address, so an empty string is a deletion.
              () => updateShippingAddress({
                orderId: order.id,
                address1: address.address1.trim(),
                address2: address.address2.trim() || undefined,
                city: address.city.trim(),
                state: address.state.trim() || undefined,
                postalCode: address.postalCode.trim(),
              })
            )}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default OrderActions;
